import {
  isBonusTimeUpSubmitType,
  type BonusGameType,
} from '../constants/bonusConstants';

export type BonusTimeUpFlushScores = {
  fastFingers: number;
  catchTheTrain: number;
  saveTheAnimals: number;
  fastTypingAnswer: string;
  longestWordAnswer: string;
};

export type BonusTimeUpFlushPayload = {
  answer: string;
  points: number;
  /** Arcade games allow BE to update an existing submission; typing is submit-once. */
  allowResend: boolean;
};

/**
 * Build the authoritative end-of-round flush payload for timer/arcade bonuses.
 * Parent should emit this on bonus-time-up (and local time-up) so scoring does not
 * depend only on each game child's disabled effect.
 */
export function buildBonusTimeUpFlushPayload(
  bonusGameType: string | undefined | null,
  scores: BonusTimeUpFlushScores
): BonusTimeUpFlushPayload | null {
  if (!isBonusTimeUpSubmitType(bonusGameType)) return null;

  switch (bonusGameType as BonusGameType) {
    case 'fast-fingers':
      return {
        answer: 'time-up',
        points: Math.max(0, Math.floor(scores.fastFingers)),
        allowResend: true,
      };
    case 'catch-the-train':
      return {
        answer: 'time-up',
        points: Math.max(0, Math.floor(scores.catchTheTrain)),
        allowResend: true,
      };
    case 'catch-me-if-you-can':
      return {
        answer: 'time-up',
        points: Math.max(0, Math.floor(scores.saveTheAnimals)),
        allowResend: true,
      };
    case 'fast-typing': {
      const answer = (scores.fastTypingAnswer ?? '').trim();
      return {
        // Empty answer still registers a submission so grace does not wait forever.
        answer: answer.length > 0 ? answer : 'time-up',
        // BE computes points from text; FE must not show this 0 before ack.
        points: 0,
        // Allow grace re-nudge / better text after an empty flush (BE takes max points).
        allowResend: true,
      };
    }
    case 'longest-word': {
      const answer = (scores.longestWordAnswer ?? '').trim();
      return {
        answer: answer.length > 0 ? answer : 'time-up',
        points: 0,
        allowResend: true,
      };
    }
    default:
      return null;
  }
}

/**
 * Points to show in the player overlay from a flush payload.
 * Typing waits for bonus-answer-ack (flush.points is always 0 by design).
 */
export function flushPointsForPlayerOverlay(
  bonusGameType: string | undefined | null,
  flush: BonusTimeUpFlushPayload
): number | undefined {
  if (bonusGameType === 'fast-typing' || bonusGameType === 'longest-word') return undefined;
  return flush.points;
}
